// Geography reference data for AddressFields.
//
// Country list is curated — top markets + every country that matters
// for SMB-ERP customer / supplier records. Anything not in the list
// can still be typed via the "Other (type code)" escape hatch (the
// underlying value remains a 2-letter ISO 3166-1 alpha-2 code). State
// lists ship for India today; other regimes will plug in as they
// ship (Slice 18 multi-country).

export interface CountryOption {
  /** ISO 3166-1 alpha-2 (uppercase). */
  code: string;
  name: string;
}

export const COUNTRIES: CountryOption[] = [
  { code: 'IN', name: 'India' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SG', name: 'Singapore' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'NP', name: 'Nepal' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'CN', name: 'China' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'KE', name: 'Kenya' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'EG', name: 'Egypt' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'QA', name: 'Qatar' },
  { code: 'OM', name: 'Oman' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'KW', name: 'Kuwait' },
];

export interface StateOption {
  /** 2-letter India state code (GSTIN-compatible). */
  code: string;
  name: string;
  /** UT vs State — drives a small badge in the picker. */
  kind: 'state' | 'ut';
}

// India: 28 states + 8 union territories. Codes match the official
// GSTIN state-code conventions. Names sorted alphabetically — UTs are
// merged into the same list with a kind discriminator so a single
// picker covers both.
export const INDIA_STATES: StateOption[] = [
  { code: 'AN', name: 'Andaman and Nicobar Islands', kind: 'ut' },
  { code: 'AP', name: 'Andhra Pradesh', kind: 'state' },
  { code: 'AR', name: 'Arunachal Pradesh', kind: 'state' },
  { code: 'AS', name: 'Assam', kind: 'state' },
  { code: 'BR', name: 'Bihar', kind: 'state' },
  { code: 'CH', name: 'Chandigarh', kind: 'ut' },
  { code: 'CG', name: 'Chhattisgarh', kind: 'state' },
  { code: 'DN', name: 'Dadra and Nagar Haveli and Daman and Diu', kind: 'ut' },
  { code: 'DL', name: 'Delhi', kind: 'ut' },
  { code: 'GA', name: 'Goa', kind: 'state' },
  { code: 'GJ', name: 'Gujarat', kind: 'state' },
  { code: 'HR', name: 'Haryana', kind: 'state' },
  { code: 'HP', name: 'Himachal Pradesh', kind: 'state' },
  { code: 'JK', name: 'Jammu and Kashmir', kind: 'ut' },
  { code: 'JH', name: 'Jharkhand', kind: 'state' },
  { code: 'KA', name: 'Karnataka', kind: 'state' },
  { code: 'KL', name: 'Kerala', kind: 'state' },
  { code: 'LA', name: 'Ladakh', kind: 'ut' },
  { code: 'LD', name: 'Lakshadweep', kind: 'ut' },
  { code: 'MP', name: 'Madhya Pradesh', kind: 'state' },
  { code: 'MH', name: 'Maharashtra', kind: 'state' },
  { code: 'MN', name: 'Manipur', kind: 'state' },
  { code: 'ML', name: 'Meghalaya', kind: 'state' },
  { code: 'MZ', name: 'Mizoram', kind: 'state' },
  { code: 'NL', name: 'Nagaland', kind: 'state' },
  { code: 'OD', name: 'Odisha', kind: 'state' },
  { code: 'PY', name: 'Puducherry', kind: 'ut' },
  { code: 'PB', name: 'Punjab', kind: 'state' },
  { code: 'RJ', name: 'Rajasthan', kind: 'state' },
  { code: 'SK', name: 'Sikkim', kind: 'state' },
  { code: 'TN', name: 'Tamil Nadu', kind: 'state' },
  { code: 'TG', name: 'Telangana', kind: 'state' },
  { code: 'TR', name: 'Tripura', kind: 'state' },
  { code: 'UP', name: 'Uttar Pradesh', kind: 'state' },
  { code: 'UK', name: 'Uttarakhand', kind: 'state' },
  { code: 'WB', name: 'West Bengal', kind: 'state' },
];

/**
 * Returns the canonical state list for a country, or null if we don't
 * ship one yet — caller renders a free-text input as the fallback.
 */
export function statesFor(country: string): StateOption[] | null {
  const c = country.toUpperCase();
  if (c === 'IN') return INDIA_STATES;
  return null;
}

/**
 * Per-country postal-code metadata. Drives the input label + pattern +
 * placeholder. Defaults are intentionally permissive — the field is
 * never required at the type level, the BE validates per-tenant.
 */
export interface PostalCodeSpec {
  label: string;
  placeholder: string;
  /** Regex string for HTML5 pattern attribute. */
  pattern?: string;
  /** maxLength for the input. */
  maxLength?: number;
  /** Help line under the field. */
  hint?: string;
}

const POSTAL_CODE_SPECS: Record<string, PostalCodeSpec> = {
  IN: {
    label: 'PIN code',
    placeholder: '400001',
    pattern: '\\d{6}',
    maxLength: 6,
    hint: '6-digit Indian PIN.',
  },
  US: {
    label: 'ZIP code',
    placeholder: '10001',
    pattern: '\\d{5}(-\\d{4})?',
    maxLength: 10,
    hint: '5-digit ZIP, or ZIP+4.',
  },
  GB: {
    label: 'Postcode',
    placeholder: 'SW1A 1AA',
    maxLength: 10,
  },
  CA: {
    label: 'Postal code',
    placeholder: 'M5H 2N2',
    maxLength: 7,
  },
};

export function postalCodeSpecFor(country: string): PostalCodeSpec {
  return (
    POSTAL_CODE_SPECS[country.toUpperCase()] ?? {
      label: 'Postal code',
      placeholder: '',
      maxLength: 20,
    }
  );
}

// City suggestions per India state — top business cities + district HQs.
// Datalist-driven typeahead: the operator can type any value, but the
// browser surfaces hits from this list as they type. State-scoped so a
// "Pune" suggestion only shows when MH is the picked state.
//
// Coverage is intentionally limited to top 8–15 cities per state; this
// is for input ergonomics, not a definitive geography dataset. New
// entries belong here, not inlined at call sites.
export const INDIA_CITIES_BY_STATE: Record<string, string[]> = {
  AN: ['Port Blair'],
  AP: ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore', 'Tirupati', 'Kakinada', 'Rajahmundry', 'Kurnool', 'Anantapur'],
  AR: ['Itanagar', 'Naharlagun', 'Pasighat'],
  AS: ['Guwahati', 'Dibrugarh', 'Silchar', 'Jorhat', 'Nagaon', 'Tinsukia', 'Tezpur'],
  BR: ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Purnia', 'Darbhanga', 'Arrah', 'Begusarai'],
  CH: ['Chandigarh'],
  CG: ['Raipur', 'Bhilai', 'Bilaspur', 'Korba', 'Durg', 'Rajnandgaon'],
  DN: ['Daman', 'Diu', 'Silvassa'],
  DL: ['New Delhi', 'Delhi', 'Dwarka', 'Rohini', 'Saket', 'Karol Bagh'],
  GA: ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa', 'Ponda'],
  GJ: ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar', 'Junagadh', 'Gandhinagar', 'Anand'],
  HR: ['Gurugram', 'Faridabad', 'Panipat', 'Karnal', 'Ambala', 'Hisar', 'Rohtak', 'Sonipat', 'Yamunanagar'],
  HP: ['Shimla', 'Dharamshala', 'Mandi', 'Solan', 'Manali', 'Kullu', 'Una'],
  JK: ['Srinagar', 'Jammu', 'Anantnag', 'Baramulla', 'Udhampur'],
  JH: ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro', 'Hazaribagh', 'Deoghar'],
  KA: ['Bengaluru', 'Mysuru', 'Mangaluru', 'Hubballi', 'Belagavi', 'Davangere', 'Tumakuru', 'Shivamogga', 'Ballari'],
  KL: ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur', 'Kollam', 'Kannur', 'Alappuzha', 'Palakkad'],
  LA: ['Leh', 'Kargil'],
  LD: ['Kavaratti'],
  MP: ['Indore', 'Bhopal', 'Jabalpur', 'Gwalior', 'Ujjain', 'Sagar', 'Dewas', 'Satna', 'Ratlam'],
  MH: ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Kolhapur', 'Navi Mumbai', 'Amravati', 'Sangli', 'Jalgaon'],
  MN: ['Imphal', 'Thoubal'],
  ML: ['Shillong', 'Tura'],
  MZ: ['Aizawl', 'Lunglei'],
  NL: ['Kohima', 'Dimapur', 'Mokokchung'],
  OD: ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Berhampur', 'Sambalpur', 'Puri', 'Balasore'],
  PY: ['Puducherry', 'Karaikal', 'Yanam', 'Mahe'],
  PB: ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda', 'Mohali', 'Hoshiarpur', 'Pathankot'],
  RJ: ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer', 'Bikaner', 'Alwar', 'Bhilwara', 'Sikar'],
  SK: ['Gangtok', 'Namchi'],
  TN: ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli', 'Erode', 'Vellore', 'Tiruppur', 'Thoothukudi'],
  TG: ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Khammam', 'Mahbubnagar', 'Secunderabad'],
  TR: ['Agartala', 'Udaipur', 'Dharmanagar'],
  UP: ['Lucknow', 'Kanpur', 'Agra', 'Varanasi', 'Ghaziabad', 'Noida', 'Meerut', 'Prayagraj', 'Bareilly', 'Aligarh', 'Moradabad', 'Saharanpur', 'Gorakhpur', 'Jhansi'],
  UK: ['Dehradun', 'Haridwar', 'Roorkee', 'Haldwani', 'Nainital', 'Rudrapur', 'Rishikesh'],
  WB: ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri', 'Bardhaman', 'Malda', 'Kharagpur', 'Haldia'],
};

/**
 * Top cities for a given country + state. Returns null when no
 * curated list ships for that combo — caller falls back to a plain
 * free-text input (no suggestion list). Currently India-only;
 * extending to other countries is additive.
 */
export function citiesFor(country: string, state?: string | null): string[] | null {
  if (country.toUpperCase() !== 'IN') return null;
  if (!state) return null;
  return INDIA_CITIES_BY_STATE[state.toUpperCase()] ?? null;
}
